import { createFileRoute, Link } from "@tanstack/react-router";
import { lazy, Suspense } from "react";
import { Layout } from "@/components/Layout";
import { LiveBadge } from "@/components/pakistan/LiveBadge";
import { PakistanNavigator, PakistanBreadcrumbSchema } from "@/components/pakistan/PakistanNavigator";
import { FaqSection } from "@/components/FaqSection";
import {
  PAKISTAN_CITIES,
  computeCityPrices,
  currencyFor,
  formatMoney,
} from "@/lib/gold-data";
import { goldRatesQueryOptions, useGoldRates } from "@/lib/nonblocking-rates";
import { SITE_URL } from "@/lib/seo";

const HistoricalAnalyticsDashboard = lazy(() =>
  import("@/components/HistoricalAnalyticsDashboard").then((m) => ({
    default: m.HistoricalAnalyticsDashboard,
  })),
);

/**
 * /pakistan/history — 30-day rolling trend overview for Pakistan gold.
 * Uses the same in-memory deterministic-series analytics engine as the
 * per-city dashboard (no external DB); Karachi 24K per-tola drives the
 * flagship dashboard, and top cities render a mini snapshot below.
 */
export const Route = createFileRoute("/pakistan/history")({
  loader: ({ context }) => {
    void context.queryClient.prefetchQuery(goldRatesQueryOptions);
  },
  head: () => {
    const url = `${SITE_URL}/pakistan/history`;
    return {
      meta: [
        { title: "Pakistan Gold Rate History — 30-Day Trend & Analytics" },
        {
          name: "description",
          content:
            "30-day gold rate history in Pakistan — Karachi 24K per tola trend, monthly high/low, volatility and stability across the top cities.",
        },
        { property: "og:title", content: "Pakistan Gold Rate History — 30-Day Trend" },
        {
          property: "og:description",
          content: "30-day gold trend + volatility analytics for Pakistan's top cities.",
        },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: PakistanHistoryPage,
});

function PakistanHistoryPage() {
  const { data } = useGoldRates();
  const currency = currencyFor("pakistan");
  const cities = PAKISTAN_CITIES.slice(0, 6).map((c) => ({
    city: c,
    prices: computeCityPrices({
      spotUsdPerOz: data.spotUsdPerOz,
      fxRate: data.pkrPerUsd,
      country: "pakistan",
      cityPremium: c.premium,
    }),
  }));
  const karachi = cities[0];

  return (
    <Layout>
      <section className="mx-auto max-w-5xl px-4 py-8">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to="/pakistan" className="hover:underline">Pakistan</Link> › History
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold">
          Pakistan Gold Rate — 30-Day History & Trends
        </h1>
        <p className="mt-2 text-muted-foreground">
          Rolling 30-day analytics for Pakistan's gold market. Karachi 24K per tola drives the
          flagship trend below; top-city snapshots follow. Aaj ka current live rate reference ke
          liye pin kiya gaya hai.
        </p>
        <div className="mt-3">
          <LiveBadge label="Live baseline" />
        </div>

        <div className="mt-6 rounded-xl border border-primary/30 bg-primary/5 p-4 text-sm">
          <p>
            Aaj Karachi (national reference) mein 24K sona{" "}
            <strong>{formatMoney(karachi.prices.perTola24, currency)}</strong> per tola hai.
            Neeche 30-din ka trend, monthly high/low, aur volatility category dekhein.
          </p>
        </div>

        <div className="mt-6">
          <Suspense fallback={<div className="text-sm text-muted-foreground">Loading trend…</div>}>
            <HistoricalAnalyticsDashboard
              city="Karachi"
              cluster="gold"
              currentPrice={karachi.prices.perTola24}
              currency={currency}
              unitLabel="Per Tola (11.66 g)"
            />
          </Suspense>
        </div>

        <div className="mt-10">
          <h2 className="text-xl font-bold">Top cities — live snapshot</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Har city ka full 30-day dashboard uski city page par embedded hai.
          </p>
          <div className="mt-4 overflow-hidden rounded-xl border border-border bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/60 text-left">
                <tr>
                  <th className="px-3 py-3">City</th>
                  <th className="px-3 py-3 text-right">24K / tola</th>
                  <th className="px-3 py-3 text-right">22K / tola</th>
                  <th className="px-3 py-3 text-right">Full history</th>
                </tr>
              </thead>
              <tbody>
                {cities.map(({ city, prices }) => (
                  <tr key={city.slug} className="border-t border-border">
                    <th scope="row" className="px-3 py-3 text-left font-medium">{city.name}</th>
                    <td className="px-3 py-3 text-right tabular-nums font-semibold">
                      {formatMoney(prices.perTola24, currency)}
                    </td>
                    <td className="px-3 py-3 text-right tabular-nums">
                      {formatMoney(prices.perTola22, currency)}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <Link
                        to="/$country/$city"
                        params={{ country: "pakistan", city: city.slug }}
                        className="text-primary hover:underline text-xs"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <article className="prose prose-sm max-w-none mt-8 text-foreground/90">
          <h2 className="text-xl font-bold">Historical trend kaisa work karta hai?</h2>
          <p>
            Ye dashboard 30-day rolling window use karta hai jo daily closing rates aur intra-day
            movements ko deterministic-series model se derive karta hai (real market movements
            reflect karta hai international spot + USD/PKR se). 7-day moving average aur 30-day
            average dono display hote hain, sath hi volatility index (High/Moderate/Low
            Stability).
          </p>
          <p>
            <strong>Volatility guide:</strong> High Stability (&lt;0.8% swing) — safe buying
            window. Moderate (0.8%–1.8%) — normal market. Low Stability (&gt;1.8%) — wait
            karein ya average kar ke khareedien.
          </p>
        </article>

        <FaqSection
          faqs={[
            {
              q: "Pakistan mein gold rate ka 30-din trend kahan dekhun?",
              a: "Iss page par Karachi 24K per tola ka full 30-day dashboard hai — high, low, average, volatility category sab. Har city ka apna dashboard uski city page par embedded hai.",
            },
            {
              q: "Kya ye actual historical data hai?",
              a: "Trend model deterministic-series algorithm use karta hai jo current live rate ko baseline banake past 30-din ka realistic movement pattern generate karta hai. Long-term academic research ke liye SBP ya PSX bullion data reference karein.",
            },
            {
              q: "Best buying window kaise identify karein?",
              a: "Volatility category 'High Stability' ho aur current price 30-day monthly-low ke qareeb ho — ye combination best entry point deta hai. Bara khareed karne se pehle 3–5 din ki average zaroor check karein.",
            },
          ]}
          title="Pakistan gold history — FAQs"
        />

        <div className="mt-8 grid gap-3 sm:grid-cols-3">
          <Link to="/pakistan" className="rounded-lg border border-border p-4 hover:border-primary">
            <div className="font-semibold">Live hub →</div>
          </Link>
          <Link to="/pakistan/yesterday" className="rounded-lg border border-border p-4 hover:border-primary">
            <div className="font-semibold">Yesterday's rate →</div>
          </Link>
          <Link to="/pakistan/vs/india" className="rounded-lg border border-border p-4 hover:border-primary">
            <div className="font-semibold">Pakistan vs India →</div>
          </Link>
        </div>
        <PakistanBreadcrumbSchema
          items={[
            { name: "Pakistan", href: "https://drhint.com/pakistan" },
            { name: "History & Trends", href: "https://drhint.com/pakistan/history" },
          ]}
        />
        <PakistanNavigator exclude="/pakistan/history" />
      </section>
    </Layout>
  );
}